import _ from 'lodash'

/**
 * 列サイズを計算するコンポーネント
 */
export default class ColumnSizes {
  private readonly totalSize: number
  private readonly sizes: number[]

  /**
   * @param {number[]} sizes - 各列のサイズ
   */
  constructor(itemSizes: number[]) {
    this.sizes = itemSizes.concat()  // clone Array
    this.totalSize = itemSizes.reduce((v1, v2) => v1 + v2)
  }

  get itemSizes(): number[] {
    return this.sizes.concat()  // clone Array
  }

  get itemCount(): number {
    return this.sizes.length
  }

  /**
   * 列サイズを自動計算してインスタンスを生成する。
   * @param {number} totalSize - 各列のサイズの合計
   * @param {number} columnCount - 列数
   * @return {ColumnSizes} - 自動計算したサイズ
   */
  static ofNaturalSizes(totalSize: number, columnCount: number): ColumnSizes {
    // 各列のサイズを均等に割り振る
    const sizes = _.times(columnCount, i => Math.floor((totalSize + i) / columnCount)).reverse()
    return new ColumnSizes(sizes)
  }

  toNaturalSize(): ColumnSizes {
    return ColumnSizes.ofNaturalSizes(this.totalSize, this.itemCount)
  }

  /**
   * アイテムを追加したときのサイズを返す
   * @param {number} index - アイテムを追加する位置
   * @return {ColumnSizes} - 変更後のサイズ
   */
  insertItemAt(index: number): ColumnSizes {
    const itemSizes = this.sizes.concat()  // clone Array
    const averageSize = Math.floor(this.totalSize / (this.itemCount + 1))
    let requiredSize = averageSize

    // 右から左に向かって、必要なサイズを分けてもらう
    // TODO #72230
    for (let i = itemSizes.length - 1; i >= 0 && requiredSize > 0; i--) {
      const excessSize = Math.min(itemSizes[i] - averageSize, requiredSize)
      if (excessSize > 0) {
        itemSizes[i] -= excessSize
        requiredSize -= excessSize
      }
    }
    if (requiredSize > 0) {
      throw new Error('アイテムを追加するのに必要なサイズが確保できませんでした')
    }
    itemSizes.splice(index, 0, averageSize)
    return new ColumnSizes(itemSizes)
  }

  /**
   * アイテムを削除したときのサイズを返す
   * @param {number} index - アイテムを削除する位置
   * @return {ColumnSizes} - 変更後のサイズ
   */
  removeItemAt(index: number): ColumnSizes {
    const itemSizes = this.sizes.concat()  // clone Array
    const averageSize = Math.floor(this.totalSize / (this.itemCount - 1))
    const restSize = itemSizes[index]

    // 削除した要素の右側、または左側に余ったサイズを与える
    if ((index + 1) <= (itemSizes.length - 1)) {
      itemSizes[index + 1] += restSize
    } else {
      itemSizes[index - 1] += restSize
    }
    itemSizes.splice(index, 1)
    return new ColumnSizes(itemSizes)
  }
}
